'use client';

import NavItem from '@/components/layout/Navigation/sidebar/nav-item';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@heidi/design-system/collapsible';
import { useTranslations } from '@heidi/runtime/i18n';
import { MoreHorizontal } from 'lucide-react';
import { useState } from 'react';

interface ExpandableNavItemGroupProps {
  /** Children nav items to show when expanded */
  children: React.ReactNode;
  /** Whether the item should be open by default */
  defaultOpen?: boolean;
  /** When false, renders children in a fragment without the expandable UI */
  enabled?: boolean;
  /** Whether this item is currently active */
  isActive?: boolean;
  /** Test ID for the nav item */
  testId?: string;
}

/**
 * ExpandableNavItem is a collapsible navigation item that can show/hide child nav items.
 * e.g. grouping navigation items like Tasks and Templates under a "More" menu.
 */
export const ExpandableNavItemGroup = ({
  children,
  defaultOpen = false,
  enabled = true,
  isActive = false,
  testId,
}: ExpandableNavItemGroupProps) => {
  const [isOpen, setIsOpen] = useState(defaultOpen || isActive);
  const { t } = useTranslations();

  if (!enabled) {
    return <>{children}</>;
  }

  const label = isOpen ? t('common.less') : t('common.more');

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setIsOpen}
      className="group/collapsible w-full"
      data-testid={testId}
    >
      <CollapsibleContent className="pb-2">{children}</CollapsibleContent>
      <CollapsibleTrigger asChild>
        <NavItem isActive={isActive} label={label} icon={<MoreHorizontal />} />
      </CollapsibleTrigger>
    </Collapsible>
  );
};
